import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { BrainCircuit, Map, UserCircle, Phone, CheckSquare, Globe, Megaphone, Users, Database, Mic, ShieldCheck, FileText, ArrowRight } from 'lucide-react';
import { FeaturesGrid } from '../components/FeaturesGrid';
/*
  SEO SPECIFICATION:
  Title: MISTR Features — Every AI Automation Tool Your Business Needs | MindStream.ing
  Meta Desc: Voice AI, email monitoring, PPC management, CRM automation, project tracking, self-managing website and more. One AI. Every role.
  Primary KW: AI business automation features
*/
export function FeaturesHub() {
  const [activeSection, setActiveSection] = useState('ai-brain');
  // Handle scroll spy for sticky nav
  useEffect(() => {
    const handleScroll = () => {
      const sections = ['ai-brain', 'your-business', 'your-growth', 'all-streams'];
      for (const section of sections) {
        const element = document.getElementById(section);
        if (element) {
          const rect = element.getBoundingClientRect();
          // If the top of the section is near the top of the viewport
          if (rect.top <= 200 && rect.bottom >= 200) {
            setActiveSection(section);
            break;
          }
        }
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      const y = element.getBoundingClientRect().top + window.scrollY - 140; // Offset for fixed navs
      window.scrollTo({
        top: y,
        behavior: 'smooth'
      });
    }
  };
  const sections = [{
    id: 'ai-brain',
    label: 'Your AI Brain'
  }, {
    id: 'your-business',
    label: 'Your Business'
  }, {
    id: 'your-growth',
    label: 'Your Growth'
  }, {
    id: 'all-streams',
    label: 'All Streams'
  }];
  return <main className="pt-20" data-id="element-1607" data-mp="1ij9kviyg4rl3">
      {/* Hero */}
      <section className="relative pt-24 pb-16 px-6 bg-hero-gradient text-center overflow-hidden" data-id="element-1608" data-mp="1ijhvf473jkt4">
        {/* Ambient Glow Orbs */}
        <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-purple/20 rounded-full blur-[120px] opacity-30 pointer-events-none" data-id="element-1609" data-mp="1dpwyzgl5iyw1" />
        <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-teal/20 rounded-full blur-[100px] opacity-20 pointer-events-none" data-id="element-1610" data-mp="14smk7l94krmd" />

        {/* Grid Pattern Overlay */}
        <div className="absolute inset-0 opacity-[0.03] pointer-events-none" style={{
        backgroundImage: `radial-gradient(circle, white 1px, transparent 1px)`,
        backgroundSize: '40px 40px'
      }} data-id="element-1611" data-mp="ut5jli8qd3x9" />

        <div className="inner-container max-w-4xl mx-auto relative z-10" data-id="element-1612" data-mp="2p6n5z1xefuqp">
          <h1 className="font-poppins font-extrabold text-5xl md:text-6xl leading-[1.1] mb-6" data-id="element-1613" data-mp="2k9n0as4gou04">
            One AI. Every Role.
            <br data-id="element-1614" data-mp="2sn9keldxtsec" />
            <span className="text-gradient" data-id="element-1615" data-mp="2snj8g3ff9phe">Every Feature.</span>
          </h1>
          <p className="text-lg text-steel max-w-2xl mx-auto" data-id="element-1616" data-mp="2wp4hfdou5jmb">
            Explore the complete ecosystem of Streams that power MindStream.ing.
          </p>
        </div>
      </section>

      {/* Sticky Pill Navigation */}
      <div className="sticky top-20 z-40 bg-dark-bg/80 backdrop-blur-md border-y border-white/10 py-4 px-6" data-id="element-1617" data-mp="29qhu3rzzad0s">
        <div className="max-w-4xl mx-auto flex flex-wrap justify-center gap-2 md:gap-4" data-id="element-1618" data-mp="2drtkhzqof2a1">
          {sections.map(section => <button key={section.id} onClick={() => scrollToSection(section.id)} className={`px-4 md:px-6 py-2 rounded-full text-sm font-medium transition-all ${activeSection === section.id ? 'bg-teal text-white shadow-lg shadow-teal/20' : 'bg-white/5 text-steel hover:text-white hover:bg-white/10'}`} data-id="element-1619" data-mp="2iq4tk895d8r7">
              {section.label}
            </button>)}
        </div>
      </div>

      {/* Section 1: Your AI Brain */}
      <section id="ai-brain" className="section-padding bg-dark-bg scroll-mt-32 relative" data-id="element-1620" data-mp="16ynphnsyf6ny">
        <div className="inner-container relative z-10" data-id="element-1621" data-mp="1kyq676pizu1m">
          <div className="mb-12" data-id="element-1622" data-mp="1fyk8b8snxbb3">
            <span className="eyebrow" data-id="element-1623" data-mp="1fxiuwxo6ccq7">Core Intelligence</span>
            <h2 className="h2-title" data-id="element-1624" data-mp="1uvurl4bjrlhd">Your AI Brain</h2>
            <p className="text-steel max-w-2xl" data-id="element-1625" data-mp="1uu9wndlbm0iz">
              The foundational intelligence that learns your business, maps your
              operations, and speaks in your voice.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6" data-id="element-1626" data-mp="23nd517h3zn6r">
            <FeatureDetailCard icon={<BrainCircuit size={28} data-id="element-1628" data-mp="1zncsxthhfo2v" />} title="ThinkTank" desc="Your AI-powered team brain. Every conversation, every decision, every file — searchable and accessible in one intelligent workspace." link="/think-tank" data-id="element-1627" data-mp="1zmb48m7jn32o" />
            <FeatureDetailCard icon={<Map size={28} data-id="element-1630" data-mp="2s6yeljgwqujt" />} title="Live Mind Maps" desc="A self-updating visual of every project, client, team member, and task — live, in real time, always current." link="/live-mind-maps" data-id="element-1629" data-mp="2sa2sj8k6d4no" />
            <FeatureDetailCard icon={<UserCircle size={28} data-id="element-1632" data-mp="2n83odwgm23po" />} title="Persona Builder" desc="3 conversations and MR knows your brand voice, your clients, your history. Every output sounds like you." link="/persona-builder" data-id="element-1631" data-mp="2n959yuv968ln" />
            <FeatureDetailCard icon={<Phone size={28} data-id="element-1634" data-mp="1zlx348i6erhi" />} title="Dedicated Line" desc="A dedicated phone number for every client relationship. MR handles every call — intake, follow-up, escalation — automatically." link="/dedicated-line" data-id="element-1633" data-mp="1zkvro2x902z9" />
          </div>
        </div>
      </section>

      {/* Gradient Divider */}
      <div className="h-px bg-gradient-to-r from-transparent via-purple/30 to-transparent" data-id="element-1635" data-mp="12a8ohsgto09" />

      {/* Section 2: Your Business */}
      <section id="your-business" className="section-padding bg-dark-bg scroll-mt-32 relative overflow-hidden" data-id="element-1636" data-mp="3sg6hplejzejv">
        {/* Ambient Glow */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-teal/10 rounded-full blur-[120px] opacity-30 pointer-events-none" data-id="element-1637" data-mp="3ijbl581ubo88" />

        <div className="inner-container relative z-10" data-id="element-1638" data-mp="jz1zdcac75ec">
          <div className="mb-12" data-id="element-1639" data-mp="f3ct6q3taq92">
            <span className="eyebrow" data-id="element-1640" data-mp="f08irazr6y9y">Operations</span>
            <h2 className="h2-title" data-id="element-1641" data-mp="a2720wpsvb1n">Your Business</h2>
            <p className="text-steel max-w-2xl" data-id="element-1642" data-mp="a5314oddw5ro">
              The Streams that execute your daily operations, manage projects,
              and handle client onboarding.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6" data-id="element-1643" data-mp="2qrvqcqq4qv6x">
            <FeatureDetailCard icon={<CheckSquare size={28} data-id="element-1645" data-mp="2qu8gvs9yedvl" />} title="AI Project Manager" desc="MR creates tasks from every conversation, assigns them, follows up, and delivers results — automatically." link="/project-manager" data-id="element-1644" data-mp="2qr2tqikvxm2i" />
            <FeatureDetailCard icon={<Globe size={28} data-id="element-1647" data-mp="1f8ophzvz9gio" />} title="Self-Building Website" desc="Your website updates itself as your business evolves. New offer? It's live. New copy? Already updated." link="/self-building-website" data-id="element-1646" data-mp="1f9q5miruot79" />
            <FeatureDetailCard icon={<Megaphone size={28} data-id="element-1649" data-mp="1u18tf2qybs16" />} title="Self-Marketing" desc="Content written, ads optimized, social posted, SEO handled — MR manages the whole marketing engine." link="/self-running-marketing" data-id="element-1648" data-mp="1ty30tdj4680p" />
            <FeatureDetailCard icon={<Users size={28} data-id="element-1651" data-mp="3p2qt7yh6s80" />} title="Onboarding Agents" desc="MR handles the entire client onboarding sequence — intake, docs, setup, communication — automatically." link="/onboarding-agents" data-id="element-1650" data-mp="3o1fd2djs3pr" />
          </div>
        </div>
      </section>

      {/* Gradient Divider */}
      <div className="h-px bg-gradient-to-r from-transparent via-teal/30 to-transparent" data-id="element-1652" data-mp="2k953z3eijvyj" />

      {/* Section 3: Your Growth */}
      <section id="your-growth" className="section-padding bg-dark-bg scroll-mt-32 relative" data-id="element-1653" data-mp="2fbd3vm3pt3j9">
        <div className="inner-container relative z-10" data-id="element-1654" data-mp="1rl99m5cf2g4x">
          <div className="mb-12" data-id="element-1655" data-mp="1njnpq01gt1l8">
            <span className="eyebrow" data-id="element-1656" data-mp="1nkgy15g2nz5h">Expansion</span>
            <h2 className="h2-title" data-id="element-1657" data-mp="21ingbd8jfqk1">Your Growth</h2>
            <p className="text-steel max-w-2xl" data-id="element-1658" data-mp="21ki0gpbzjen2">
              The Streams that drive revenue, protect your reputation, and
              manage your pipeline.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6" data-id="element-1659" data-mp="3cwitwhjsnr95">
            <FeatureDetailCard icon={<Database size={28} data-id="element-1661" data-mp="37y7l8a58jmsi" />} title="Self-Managing CRM" desc="Your CRM stays clean, contacts stay updated, and follow-ups happen automatically. MR runs your entire pipeline." link="/self-managing-crm" data-id="element-1660" data-mp="37z9616c1znnf" />
            <FeatureDetailCard icon={<Mic size={28} data-id="element-1663" data-mp="kruxtqfgav2d" />} title="Voice Agents" desc="MR's voice agents take calls, answer questions, schedule appointments, and escalate when needed — 24/7." link="/voice-agents" data-id="element-1662" data-mp="kqtfd0w3v6am" />
            <FeatureDetailCard icon={<ShieldCheck size={28} data-id="element-1665" data-mp="1hh2eol54kfzy" />} title="Reputation Manager" desc="MR monitors every review platform, flags negative feedback, and responds automatically in your brand voice." link="/reputation-manager" data-id="element-1664" data-mp="1dhl7nna1vaso" />
            <FeatureDetailCard icon={<FileText size={28} data-id="element-1667" data-mp="1wd1smcex1ad0" />} title="Document Generator" desc="MR generates proposals, contracts, SOPs, and reports automatically — in your brand voice, ready to send." link="/document-generator" data-id="element-1666" data-mp="1we34uk7o42wb" />
          </div>
        </div>
      </section>

      {/* Gradient Divider */}
      <div className="h-px bg-gradient-to-r from-transparent via-purple/30 to-transparent" data-id="element-1668" data-mp="25c7dtd1x35ko" />

      {/* Section 4: All Streams */}
      <section id="all-streams" className="py-24 bg-dark-bg scroll-mt-32 relative overflow-hidden" data-id="element-1669" data-mp="1vet2tsyrj8zr">
        {/* Ambient Glow */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-purple/10 rounded-full blur-[120px] opacity-30 pointer-events-none" data-id="element-1670" data-mp="1hltm5nn8v8qm" />

        <div className="inner-container px-6 mb-12 relative z-10" data-id="element-1671" data-mp="17q9rt5obwi3z">
          <span className="eyebrow" data-id="element-1672" data-mp="1co1umuqihomy">The Full Ecosystem</span>
          <h2 className="h2-title" data-id="element-1673" data-mp="1col21hijcumq">All Streams</h2>
        </div>
        {/* Reuse the homepage FeaturesGrid component */}
        <div className="relative z-10" data-id="element-1674" data-mp="yqwh69ogxnho">
          <FeaturesGrid data-id="element-1675" data-mp="13nwnmlp8cs9b" />
        </div>
      </section>

      {/* Gradient Divider */}
      <div className="h-px bg-gradient-to-r from-transparent via-teal/30 to-transparent" data-id="element-1676" data-mp="k1blop1xivph" />

      {/* Bottom CTA */}
      <section className="py-24 bg-dark-bg text-center px-6 relative overflow-hidden" data-id="element-1677" data-mp="f3s7tq958ky2">
        {/* Ambient Glow */}
        <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[600px] h-[600px] bg-teal/10 rounded-full blur-[100px] opacity-30 pointer-events-none" data-id="element-1678" data-mp="t42z2ued81jr" />

        <div className="inner-container max-w-3xl mx-auto relative z-10" data-id="element-1679" data-mp="1305z9upnpq42">
          <h2 className="font-poppins font-bold text-4xl mb-6" data-id="element-1680" data-mp="12ycs8eb4t3vj">
            Ready to see MR in action?
          </h2>
          <p className="text-steel mb-8 text-lg" data-id="element-1681" data-mp="1cq91mlio4gf2">
            Stop managing tasks and start managing growth. Let's build your
            assembly line.
          </p>
          <button className="btn-primary" data-id="element-1682" data-mp="17tilxzarzmu6">Talk to MR →</button>
        </div>
      </section>
    </main>;
}
// Helper component for the feature cards
function FeatureDetailCard({
  icon,
  title,
  desc,
  link
}: {
  icon: React.ReactNode;
  title: string;
  desc: string;
  link: string;
}) {
  return <motion.div initial={{
    opacity: 0,
    y: 20
  }} whileInView={{
    opacity: 1,
    y: 0
  }} viewport={{
    once: true
  }} className="bg-gradient-to-br from-white/[0.05] to-white/[0.02] backdrop-blur-sm border border-white/10 rounded-2xl p-8 flex flex-col h-full group hover:border-teal/40 hover:shadow-[0_0_30px_rgba(7,133,183,0.15)] transition-all" data-id="element-1683" data-mp="ecle7xklii51">
      <div className="text-teal mb-6 bg-teal/10 border border-teal/20 w-14 h-14 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform shadow-[0_0_15px_rgba(7,133,183,0.2)]" data-id="element-1684" data-mp="1240j9y3rbdjz">
        {icon}
      </div>
      <h3 className="font-poppins font-bold text-2xl mb-3 group-hover:text-teal transition-colors" data-id="element-1685" data-mp="1b3nlun882pem">
        {title}
      </h3>
      <p className="text-steel mb-8 flex-grow leading-relaxed" data-id="element-1686" data-mp="38wt7a9ocdhvs">{desc}</p>
      <Link to={link} className="text-white font-semibold flex items-center gap-2 group-hover:gap-3 group-hover:text-teal transition-all mt-auto" data-id="element-1687" data-mp="3cx3dnng1qab6">
        Learn More <ArrowRight size={16} data-id="element-1688" data-mp="30lu5i537hor4" />
      </Link>
    </motion.div>;
}